import React, { useState, useEffect } from 'react';

const STATUS_STYLE = {
    SUCCESS: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    ERROR:   { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    WARNING: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
};

const EX_STYLE = {
    NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

const TODAY    = new Date().toISOString().slice(0, 10);
const SYNC_KEY = 'NAVIABackupDropcopy_BMS_SYNC_2026_SECRET';

export default function SyncMonitor() {

    const [logs,        setLogs]        = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [dateFrom,    setDateFrom]    = useState(new Date().toISOString().slice(0,10));
    const [dateTo,      setDateTo]      = useState(new Date().toISOString().slice(0,10));
    const [exchange,    setExchange]    = useState('ALL');
    const [status,      setStatus]      = useState('ALL');

    const loadLogs = async () => {
        try {
            const params = new URLSearchParams({ key: SYNC_KEY });
            if (dateFrom)           params.append('date_from', dateFrom);
            if (dateTo)             params.append('date_to',   dateTo);
            if (exchange !== 'ALL') params.append('exchange',  exchange);
            if (status   !== 'ALL') params.append('status',    status);

            const res  = await fetch('/api/dropcopy/sync-logs?' + params.toString());
            const data = await res.json();
            setLogs(data.logs || []);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load sync logs:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadLogs(); }, []);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(loadLogs, 30000);
        return () => clearInterval(interval);
    }, [autoRefresh, dateFrom, dateTo, exchange, status]);

    const handleApply = () => { setLoading(true); loadLogs(); };
    const handleReset = () => {
        setDateFrom(TODAY); setDateTo(TODAY);
        setExchange('ALL'); setStatus('ALL');
        setTimeout(loadLogs, 100);
    };

    const fmtTime = (ts) => ts
        ? new Date(ts).toLocaleString('en-IN', {
              day:'2-digit', month:'short',
              hour:'2-digit', minute:'2-digit', second:'2-digit'
          })
        : '—';
    const fmtDur  = (ms) => !ms ? '—' : ms > 1000 ? (ms/1000).toFixed(1)+'s' : ms+'ms';
    const fmtSize = (b)  => !b  ? '—' : b > 1024*1024 ? (b/1024/1024).toFixed(1)+' MB' : Math.round(b/1024)+' KB';

    const todayStr  = TODAY;
    const todayLogs = logs.filter(l => l.trade_date?.slice(0,10) === todayStr);

    // Latest successful sync per exchange+segment
    const lastNSECM = todayLogs.find(l => l.exchange==='NSE' && l.segment==='CM' && l.status==='SUCCESS');
    const lastNSEFO = todayLogs.find(l => l.exchange==='NSE' && l.segment==='FO' && l.status==='SUCCESS');
    const lastBSECM = todayLogs.find(l => l.exchange==='BSE' && l.segment==='CM' && l.status==='SUCCESS');
    const lastBSEFO = todayLogs.find(l => l.exchange==='BSE' && l.segment==='FO' && l.status==='SUCCESS');
    const lastMCXFO = todayLogs.find(l => l.exchange==='MCX' && l.status==='SUCCESS');

    const errorCount   = todayLogs.filter(l => l.status==='ERROR').length;
    const warningCount = todayLogs.filter(l => l.status==='WARNING').length;
    const successCount = todayLogs.filter(l => l.status==='SUCCESS').length;

    const isStale = (log) => {
        if (!log) return true;
        return (Date.now() - new Date(log.log_time)) / 1000 / 60 > 5;
    };

    const SyncCard = ({ label, log, exchange: ex }) => {
        const stale  = isStale(log);
        const exStyle = EX_STYLE[ex] || {};
        return (
            <div style={{
                background: '#fff',
                border: `1px solid ${stale ? '#fecaca' : '#bbf7d0'}`,
                borderRadius: 10,
                padding: '12px 16px'
            }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{
                            fontSize:10, fontWeight:600, padding:'2px 6px', borderRadius:4,
                            background: exStyle.bg, color: exStyle.color, border:`0.5px solid ${exStyle.border}`
                        }}>{ex}</span>
                        <span style={{ fontSize:12, fontWeight:600, color:'#374151' }}>{label}</span>
                    </div>
                    <span style={{
                        fontSize:10, padding:'2px 8px', borderRadius:20, fontWeight:600,
                        background: stale ? '#fef2f2':'#f0fdf4',
                        color:      stale ? '#dc2626':'#15803d',
                        border:     `1px solid ${stale ? '#fecaca':'#bbf7d0'}`
                    }}>
                        {stale ? '⚠ STALE' : '✓ LIVE'}
                    </span>
                </div>
                {log ? (
                    <>
                        <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>
                            Last sync: <strong style={{ color:'#111' }}>{fmtTime(log.log_time)}</strong>
                        </div>
                        <div style={{ display:'flex', gap:10, fontSize:11, color:'#64748b', flexWrap:'wrap' }}>
                            <span>Trades: <strong style={{ color:'#1d4ed8' }}>{log.trades_count?.toLocaleString()}</strong></span>
                            <span>Pos: <strong style={{ color:'#15803d' }}>{log.positions_count?.toLocaleString()}</strong></span>
                            <span>{fmtDur(log.sync_duration_ms)}</span>
                        </div>
                    </>
                ) : (
                    <div style={{ fontSize:12, color:'#dc2626', marginTop:4 }}>No sync today</div>
                )}
            </div>
        );
    };

    const inputStyle = {
        padding:'6px 10px', borderRadius:6,
        border:'0.5px solid #e2e8f0', background:'#f8fafc',
        fontSize:12, color:'#0f172a', outline:'none'
    };

    return (
        <div style={{ fontFamily:"'DM Sans',-apple-system,sans-serif" }}>

            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                    <div style={{ fontSize:16, fontWeight:600, color:'#0f172a' }}>Live Sync Monitor</div>
                    <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                        DropCopy sync status — NSE CM/FO · BSE CM/FO · MCX FO → Azure
                        {lastRefresh && <span style={{ marginLeft:8 }}>· Last refresh: {fmtTime(lastRefresh)}</span>}
                    </div>
                </div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                    <label style={{ fontSize:12, color:'#64748b', display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                        Auto-refresh (30s)
                    </label>
                    <button onClick={handleApply} style={{ padding:'6px 14px', borderRadius:6, border:'1px solid #e2e8f0', background:'#fff', fontSize:12, cursor:'pointer' }}>
                        ↺ Refresh
                    </button>
                </div>
            </div>

            {/* Live status cards — NSE CM / NSE FO / BSE CM / BSE FO / MCX FO */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:16 }}>
                <SyncCard label="CM" exchange="NSE" log={lastNSECM} />
                <SyncCard label="FO" exchange="NSE" log={lastNSEFO} />
                <SyncCard label="CM" exchange="BSE" log={lastBSECM} />
                <SyncCard label="FO" exchange="BSE" log={lastBSEFO} />
                <SyncCard label="FO" exchange="MCX" log={lastMCXFO} />
            </div>

            {/* Summary stats row */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
                <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, padding:'12px 16px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>Today Events</div>
                    <div style={{ fontSize:26, fontWeight:700, color:'#1d4ed8' }}>{todayLogs.length}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>total syncs</div>
                </div>
                <div style={{ background:'#fff', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>Successful</div>
                    <div style={{ fontSize:26, fontWeight:700, color:'#15803d' }}>{successCount}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>syncs today</div>
                </div>
                <div style={{ background:'#fff', border:`1px solid ${errorCount>0?'#fecaca':'#e2e8f0'}`, borderRadius:10, padding:'12px 16px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>Errors</div>
                    <div style={{ fontSize:26, fontWeight:700, color:errorCount>0?'#dc2626':'#15803d' }}>{errorCount}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>failures today</div>
                </div>
                <div style={{ background:'#fff', border:`1px solid ${warningCount>0?'#fde68a':'#e2e8f0'}`, borderRadius:10, padding:'12px 16px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>Warnings</div>
                    <div style={{ fontSize:26, fontWeight:700, color:warningCount>0?'#d97706':'#15803d' }}>{warningCount}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>stale alerts</div>
                </div>
            </div>

            {/* Filters */}
            <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <label style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:.4 }}>From Date</label>
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <label style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:.4 }}>To Date</label>
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <label style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:.4 }}>Exchange</label>
                        <select value={exchange} onChange={e => setExchange(e.target.value)} style={inputStyle}>
                            <option value="ALL">All Exchanges</option>
                            <option value="NSE">NSE</option>
                            <option value="BSE">BSE</option>
                            <option value="MCX">MCX</option>
                        </select>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <label style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:.4 }}>Status</label>
                        <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                            <option value="ALL">All Status</option>
                            <option value="SUCCESS">Success</option>
                            <option value="ERROR">Error</option>
                            <option value="WARNING">Warning</option>
                        </select>
                    </div>
                    <button onClick={handleApply} style={{ ...inputStyle, background:'#1d4ed8', color:'#fff', border:'none', cursor:'pointer', alignSelf:'flex-end' }}>Apply</button>
                    <button onClick={handleReset} style={{ ...inputStyle, cursor:'pointer', alignSelf:'flex-end' }}>Reset</button>
                </div>
            </div>

            {/* Log Table */}
            <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
                <div style={{ padding:'12px 16px', borderBottom:'0.5px solid #e2e8f0', background:'#f8fafc', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'#374151' }}>Sync Log — Last 100 events</span>
                    <span style={{ fontSize:11, color:'#64748b' }}>{logs.length} records</span>
                </div>

                {/* Table header */}
                <div style={{ display:'grid', gridTemplateColumns:'150px 130px 55px 55px 70px 70px 70px 1fr', padding:'8px 16px', background:'#f8fafc', borderBottom:'0.5px solid #e2e8f0' }}>
                    {['Time','Source','Exch','Seg','Trades','Positions','Duration','Status / Error'].map(h => (
                        <div key={h} style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:.4 }}>{h}</div>
                    ))}
                </div>

                {loading ? (
                    <div style={{ padding:48, textAlign:'center', color:'#94a3b8', fontSize:14 }}>Loading sync logs…</div>
                ) : logs.length === 0 ? (
                    <div style={{ padding:40, textAlign:'center' }}>
                        <div style={{ fontSize:14, color:'#94a3b8', marginBottom:8 }}>No sync logs for selected filters.</div>
                        <div style={{ fontSize:12, color:'#cbd5e1' }}>Logs appear after each sync from the office server.</div>
                    </div>
                ) : (
                    logs.map((log, i) => {
                        const st = STATUS_STYLE[log.status] || STATUS_STYLE.SUCCESS;
                        const ex = EX_STYLE[log.exchange] || {};
                        return (
                            <div key={log.id} style={{
                                display:'grid',
                                gridTemplateColumns:'150px 130px 55px 55px 70px 70px 70px 1fr',
                                padding:'9px 16px',
                                alignItems:'center',
                                borderBottom: i < logs.length-1 ? '0.5px solid #f1f5f9' : 'none',
                                background: log.status==='ERROR' ? '#fff5f5' : log.status==='WARNING' ? '#fffbeb' : '#fff'
                            }}>
                                <div style={{ fontSize:11, color:'#374151' }}>{fmtTime(log.log_time)}</div>
                                <div style={{ fontSize:11, color:'#64748b' }}>{log.file_source}</div>
                                <div>
                                    <span style={{
                                        fontSize:10, fontWeight:600, padding:'2px 5px', borderRadius:4,
                                        background:ex.bg, color:ex.color, border:`0.5px solid ${ex.border}`
                                    }}>{log.exchange}</span>
                                </div>
                                <div style={{ fontSize:11, color:'#64748b' }}>{log.segment}</div>
                                <div style={{ fontSize:11, fontWeight:600, color:'#1d4ed8' }}>{log.trades_count?.toLocaleString() || '—'}</div>
                                <div style={{ fontSize:11, fontWeight:600, color:'#15803d' }}>{log.positions_count?.toLocaleString() || '—'}</div>
                                <div style={{ fontSize:11, color:'#374151' }}>{fmtDur(log.sync_duration_ms)}</div>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                    <span style={{
                                        fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:10,
                                        background:st.bg, color:st.color, border:`0.5px solid ${st.border}`
                                    }}>{log.status}</span>
                                    {log.error_message && (
                                        <span style={{ fontSize:11, color:'#dc2626' }}>{log.error_message}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer */}
            <div style={{ marginTop:12, padding:'10px 14px', background:'#f0fdf4', border:'0.5px solid #bbf7d0', borderRadius:8, fontSize:11, color:'#166534' }}>
                Direct URL:&nbsp;
                <a href={`/api/dropcopy/sync-logs?key=${SYNC_KEY}`} target="_blank" rel="noreferrer" style={{ color:'#1d4ed8', fontWeight:600 }}>
                    /api/dropcopy/sync-logs?key={SYNC_KEY}
                </a>
                &nbsp;· Auto-refreshes every 30 seconds · All exchanges: NSE CM/FO · BSE CM/FO · MCX FO
            </div>
        </div>
    );
}